import { useSyncExternalStore } from 'react';
import type { Query, QueryKey } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import { queryClient } from './constants.js';
import { supabaseClient, supabaseCoreClient } from './supabase.ts';
import { getBuildId } from './buildInfo.ts';

/**
 * Startup metadata persisted in IndexedDB without an expiry (REG-738).
 *
 * Queries opt in with `meta: persistedRelations(...)`, naming the relations
 * (`schema.relation`) their result is read from. Each persisted result carries the
 * fingerprint those relations had in core_crm.get_metadata_manifest(). On start the
 * bundle is hydrated into the query cache at once and the manifest is fetched in
 * the background; a result whose relations changed is invalidated and refetched.
 * A warm start therefore makes one small request instead of loading the metadata.
 *
 * A result is stamped only with a manifest received before its fetch started, so
 * the stored fingerprint is never newer than the data. Gated queries wait only for
 * the bundle read (see useMetadataBootReady), so a cold start fetches alongside the
 * manifest; those unstamped results are fetched once more, stamped, after the app
 * has gone idle. A cold start therefore downloads the metadata twice, off its path.
 *
 * The bundle is keyed by Supabase URL, auth user, the token's app_metadata (the
 * claims RLS reads) and the build, so a deploy or a permission change starts cold.
 */

const DB_NAME = 'sky-crm-client-cache';
const STORE_NAME = 'entries';
const DB_VERSION = 4;
const WRITE_DEBOUNCE_MS = 1000;
const MANIFEST_RETRY_DELAYS_MS = [500, 1500];
/** How long after the manifest unstamped results wait before their stamped refetch. */
const UNSTAMPED_REFETCH_DELAY_MS = 3000;
const IDLE_TIMEOUT_MS = 2000;
const cacheKeyPrefix = `metadata:${import.meta.env.VITE_SUPABASE_URL ?? 'unknown'}:`;

type Manifest = Record<string, string>;

interface PersistedQuery {
  queryKey: QueryKey;
  data: unknown;
  relations: string[];
  fingerprint: string;
  clientSide: boolean;
}

interface CacheBundle {
  queries: PersistedQuery[];
}

interface BootSnapshot {
  ready: boolean;
  /** Bumped when persisted client-side table data turns out to be stale. */
  clientSideRevision: number;
}

/** Query meta for a metadata query read from these `schema.relation`s. */
export const persistedRelations = (...relations: string[]) => ({ persistedRelations: relations });

/** Query meta for a client_side table preload, which reads `schema.<table>_view`. */
export const persistedClientSideTable = (schema: string, tableName: string) =>
  ({ persistedRelations: [`${schema}.${tableName}_view`], clientSide: true });

const queryRelations = (query: Query): string[] | null => {
  const relations = query.meta?.persistedRelations;
  return Array.isArray(relations) ? relations as string[] : null;
};

const fingerprintOf = (relations: string[], manifest: Manifest): string | null => {
  const parts = relations.map((relation) => manifest[relation]);
  return parts.every((part) => typeof part === 'string') ? parts.join('|') : null;
};

// --- IndexedDB -------------------------------------------------------------

const openCacheDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      } else if (event.oldVersion < DB_VERSION) {
        request.transaction?.objectStore(STORE_NAME).clear();
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const runTransaction = async <T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore, done: (value: T) => void) => void,
): Promise<T> => {
  const db = await openCacheDb();

  return new Promise((resolve, reject) => {
    let result: T;
    const transaction = db.transaction(STORE_NAME, mode);
    work(transaction.objectStore(STORE_NAME), (value) => { result = value; });
    transaction.oncomplete = () => {
      db.close();
      resolve(result);
    };
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
    transaction.onabort = () => {
      db.close();
      reject(transaction.error);
    };
  });
};

const readBundle = (key: string) =>
  runTransaction<CacheBundle | null>('readonly', (store, done) => {
    const request = store.get(key);
    request.onsuccess = () => done((request.result as CacheBundle | undefined) ?? null);
  });

/** Replaces the user's bundle, dropping the ones for an older build or claim set. */
const writeBundle = (key: string, userPrefix: string, bundle: CacheBundle) =>
  runTransaction<void>('readwrite', (store) => {
    const keysRequest = store.getAllKeys();
    keysRequest.onsuccess = () => {
      for (const oldKey of keysRequest.result) {
        if (typeof oldKey === 'string' && oldKey.startsWith(userPrefix) && oldKey !== key) {
          store.delete(oldKey);
        }
      }
      store.put(bundle, key);
    };
  });

/** Removes every persisted bundle. */
const clearPersistentMetadataCache = async (): Promise<void> => {
  if (typeof indexedDB === 'undefined') return;
  try {
    await runTransaction<void>('readwrite', (store) => { store.clear(); });
  } catch (error) {
    console.warn('Failed to clear persistent metadata cache:', error);
  }
};

// --- Boot state ------------------------------------------------------------

interface Scope {
  key: string;
  userPrefix: string;
}

let snapshot: BootSnapshot = { ready: false, clientSideRevision: 0 };
const listeners = new Set<() => void>();
const setSnapshot = (next: Partial<BootSnapshot>) => {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((listener) => listener());
};

let scope: Scope | null = null;
let bootGeneration = 0;
/** The manifest fetches are stamped with; null until received or when persistence is off. */
let manifest: Manifest | null = null;
/** queryHash -> what the next write stores. Survives query garbage collection. */
const entries = new Map<string, PersistedQuery>();
/** queryHash -> manifest current when that query's fetch started. */
const fetchManifests = new Map<string, Manifest | null>();
/** Queries that fetched before the manifest arrived; refetched once the app is idle. */
const unstamped = new Map<string, { queryKey: QueryKey; clientSide: boolean }>();
let writeTimer: ReturnType<typeof setTimeout> | null = null;
let unstampedTimer: ReturnType<typeof setTimeout> | null = null;

/** Refetches observed queries; client-side preloads have none, so bump their revision. */
const refetch = (queries: { queryKey: QueryKey; clientSide: boolean }[]) => {
  for (const { queryKey } of queries) void queryClient.invalidateQueries({ queryKey, exact: true });
  if (queries.some((query) => query.clientSide)) {
    setSnapshot({ clientSideRevision: snapshot.clientSideRevision + 1 });
  }
};

/** Refetches the unstamped results, once the manifest is in and the app is idle. */
const scheduleUnstampedRefetch = () => {
  if (unstampedTimer || !manifest || unstamped.size === 0) return;
  const generation = bootGeneration;
  unstampedTimer = setTimeout(() => {
    const run = () => {
      unstampedTimer = null;
      if (generation !== bootGeneration) return;
      const queries = [...unstamped.values()];
      unstamped.clear();
      refetch(queries);
    };
    if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: IDLE_TIMEOUT_MS });
    else run();
  }, UNSTAMPED_REFETCH_DELAY_MS);
};

const scheduleWrite = () => {
  const target = scope;
  if (!target || !manifest) return;
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = setTimeout(() => {
    writeTimer = null;
    if (scope !== target) return;
    writeBundle(target.key, target.userPrefix, { queries: [...entries.values()] })
      .catch((error) => console.warn('Failed to write persistent metadata cache:', error));
  }, WRITE_DEBOUNCE_MS);
};

queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== 'updated') return;
  const { query, action } = event;
  if (action.type === 'fetch') {
    fetchManifests.set(query.queryHash, manifest);
    return;
  }
  if (action.type !== 'success' || action.manual) return;
  const relations = queryRelations(query);
  if (!relations) return;

  const clientSide = query.meta?.clientSide === true;
  const stampedWith = fetchManifests.get(query.queryHash) ?? null;
  const fingerprint = stampedWith && stampedWith === manifest && fingerprintOf(relations, stampedWith);
  if (fingerprint) {
    entries.set(query.queryHash, { queryKey: query.queryKey, data: action.data, relations, fingerprint, clientSide });
    scheduleWrite();
    return;
  }
  entries.delete(query.queryHash);
  // A fetch that began before the manifest cannot be stamped; fetch it once more,
  // off the startup path, so it can be.
  if (!stampedWith) {
    unstamped.set(query.queryHash, { queryKey: query.queryKey, clientSide });
    scheduleUnstampedRefetch();
  }
});

const fetchManifest = async (): Promise<Manifest> => {
  for (let attempt = 0; ; attempt += 1) {
    const { data, error } = await (supabaseCoreClient as any).rpc('get_metadata_manifest');
    if (!error && data && typeof data === 'object') return data as Manifest;
    if (attempt >= MANIFEST_RETRY_DELAYS_MS.length) throw error ?? new Error('Empty metadata manifest');
    await new Promise((resolve) => setTimeout(resolve, MANIFEST_RETRY_DELAYS_MS[attempt]));
  }
};

const sha256 = async (value: string) => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const scopeForSession = async (session: Session): Promise<Scope> => {
  // The hook's claims are only in the token; session.user.app_metadata is not them (see useAuth).
  const claims = JSON.parse(atob(session.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  const userPrefix = `${cacheKeyPrefix}${session.user.id}:`;
  return { userPrefix, key: `${userPrefix}${await sha256(JSON.stringify(claims.app_metadata ?? null))}:${getBuildId()}` };
};

const resetBoot = (ready: boolean) => {
  bootGeneration += 1;
  scope = null;
  manifest = null;
  entries.clear();
  fetchManifests.clear();
  unstamped.clear();
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = null;
  if (unstampedTimer) clearTimeout(unstampedTimer);
  unstampedTimer = null;
  setSnapshot({ ready });
};

const boot = async (session: Session) => {
  const nextScope = await scopeForSession(session);
  if (scope?.key === nextScope.key) return;

  resetBoot(false);
  const generation = bootGeneration;
  scope = nextScope;
  const current = () => generation === bootGeneration;

  const manifestRequest = fetchManifest();
  // Keep a rejected manifest from surfacing as unhandled while the bundle is read.
  manifestRequest.catch(() => undefined);
  const bundle = typeof indexedDB === 'undefined'
    ? null
    : await readBundle(nextScope.key).catch((error) => {
        console.warn('Failed to read persistent metadata cache:', error);
        return null;
      });
  if (!current()) return;

  const hydrated: PersistedQuery[] = [];
  for (const entry of bundle?.queries ?? []) {
    if (queryClient.getQueryData(entry.queryKey) !== undefined) continue;
    queryClient.setQueryData(entry.queryKey, entry.data);
    entries.set(queryClient.getQueryCache().find({ queryKey: entry.queryKey, exact: true })!.queryHash, entry);
    hydrated.push(entry);
  }
  // Hydrated queries render now; without a bundle the queries fetch alongside the
  // manifest and are stamped by a later refetch.
  setSnapshot({ ready: true });

  let received: Manifest | null = null;
  try {
    received = await manifestRequest;
  } catch (error) {
    console.warn('Metadata manifest unavailable; persistent metadata cache disabled:', error);
  }
  if (!current()) return;

  const stale = hydrated.filter((entry) => !received || fingerprintOf(entry.relations, received) !== entry.fingerprint);
  manifest = received;
  for (const entry of stale) {
    entries.delete(queryClient.getQueryCache().find({ queryKey: entry.queryKey, exact: true })?.queryHash ?? '');
  }
  refetch(stale);
  if (stale.length > 0) scheduleWrite();
  scheduleUnstampedRefetch();
};

/**
 * Starts the metadata boot for the signed-in session and follows auth changes.
 * Call once, before the app renders.
 */
export const startMetadataBoot = () => {
  supabaseClient.auth.onAuthStateChange((event, session) => {
    // Supabase must not be called from inside this callback; defer.
    setTimeout(() => {
      if (event === 'SIGNED_OUT' || !session) {
        resetBoot(true);
        // The bundle holds data read under the user's RLS; it does not outlive the session.
        if (event === 'SIGNED_OUT') void clearPersistentMetadataCache();
        return;
      }
      boot(session).catch((error) => {
        console.warn('Metadata boot failed; persistent metadata cache disabled:', error);
        resetBoot(true);
      });
    }, 0);
  });
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

const useBootSnapshot = () => useSyncExternalStore(subscribe, () => snapshot);

/** Metadata queries are enabled only once this is true (see the module comment). */
export const useMetadataBootReady = () => useBootSnapshot().ready;

export const useClientSideRevision = () => useBootSnapshot().clientSideRevision;
